import { TICK_HZ } from './constants.js'

export const REPLAY_MAGIC = [0x41, 0x53, 0x54, 0x52] // ASTR

export function isReplayAstrHeader(buf) {
  return buf.length >= 4
    && buf[0] === REPLAY_MAGIC[0]
    && buf[1] === REPLAY_MAGIC[1]
    && buf[2] === REPLAY_MAGIC[2]
    && buf[3] === REPLAY_MAGIC[3]
}

function modeToU8(mode) {
  if (mode === 'deathmatch') return 1
  if (mode === 'ctf') return 2
  return 0
}

function u8ToMode(u8) {
  if (u8 === 1) return 'deathmatch'
  if (u8 === 2) return 'ctf'
  return 'coop'
}

function encodeKeys(keys) {
  let mask = 0
  if (keys.left) mask |= 1
  if (keys.right) mask |= 2
  if (keys.up) mask |= 4
  if (keys.down) mask |= 8
  const fp = Math.min(255, Math.max(0, Number(keys.firePresses) | 0))
  return [mask & 0xff, fp]
}

function decodeKeys(byte0, byte1) {
  return {
    left: !!(byte0 & 1),
    right: !!(byte0 & 2),
    up: !!(byte0 & 4),
    down: !!(byte0 & 8),
    firePresses: byte1
  }
}

export function policySnapshotForReplay(gameMode, policy) {
  if (gameMode === 'coop') {
    return {
      initialLives: policy.initialLives,
      shieldsEnabled: policy.shieldsEnabled === true
    }
  }
  if (gameMode === 'deathmatch') {
    return {
      roundDuration: policy.roundDuration,
      killBonus: policy.killBonus,
      asteroidCount: policy.asteroidCount,
      asteroidsEnabled: policy.asteroidsEnabled,
      shieldsEnabled: policy.shieldsEnabled === true
    }
  }
  if (gameMode === 'ctf') {
    return {
      roundDuration: policy.roundDuration,
      killBonus: policy.killBonus,
      captureBonus: policy.captureBonus,
      pickupBonus: policy.pickupBonus,
      asteroidsEnabled: policy.asteroidsEnabled,
      shieldsEnabled: policy.shieldsEnabled === true,
      asteroidCount: policy.asteroidCount
    }
  }
  return {}
}

function syntheticPlayersFromRoster(rosterIds) {
  return rosterIds.map((id, i) => ({
    id,
    name: '',
    slot: i,
    color: null,
    teamId: null,
    teamName: null
  }))
}

export class ReplayEncoder {
  constructor({
    matchSeed,
    gameMode,
    toroidalMap,
    rosterIds,
    players,
    policy,
    tickRateHz = TICK_HZ
  }) {
    this.matchSeed = matchSeed >>> 0
    this.gameMode = gameMode
    this.toroidalMap = !!toroidalMap
    this.rosterIds = [...rosterIds]
    this.extension = {
      policy: policySnapshotForReplay(gameMode, policy),
      players: players.map(p => ({
        id: p.id,
        name: p.name,
        slot: p.slot,
        color: p.color,
        teamId: p.teamId ?? null,
        teamName: p.teamName ?? null
      }))
    }
    this.tickRateHz = tickRateHz
    this.ticks = []
    this.deaths = []
  }

  appendTick(frameOneRowPerRosterPlayer) {
    this.ticks.push(frameOneRowPerRosterPlayer.map(k => ({
      left: !!k.left,
      right: !!k.right,
      up: !!k.up,
      down: !!k.down,
      firePresses: Math.min(255, Math.max(0, Number(k.firePresses) | 0))
    })))
  }

  recordDeath(tick, playerId, name, hitBy = null) {
    this.deaths.push({
      tick,
      playerId: String(playerId),
      name: String(name ?? ''),
      hitBy: hitBy != null && String(hitBy) !== '' ? String(hitBy) : ''
    })
  }

  finish() {
    const roster = this.rosterIds
    const pc = roster.length
    const duration = this.ticks.length
    const rowBytes = pc * 2
    const te = new TextEncoder()
    const extJson = JSON.stringify(this.extension)
    const extBytes = te.encode(extJson)

    let headerSize = 4 + 2 + 2 + 4 + 4 + 1 + 1 + 1
    for (const id of roster) {
      const b = te.encode(String(id))
      headerSize += 1 + b.length
    }
    headerSize += 2 + extBytes.length

    const tickBlobSize = duration * rowBytes

    let deathSize = 4
    for (const d of this.deaths) {
      const idb = te.encode(d.playerId)
      const nb = te.encode(d.name)
      const kb = te.encode(String(d.hitBy ?? ''))
      deathSize += 4 + 1 + idb.length + 2 + nb.length + 1 + kb.length
    }

    const total = headerSize + tickBlobSize + deathSize
    const out = new Uint8Array(total)
    const dv = new DataView(out.buffer)
    let o = 0

    out.set(REPLAY_MAGIC, o); o += 4
    dv.setUint16(o, 0, true); o += 2

    dv.setUint16(o, this.tickRateHz, true); o += 2
    dv.setUint32(o, duration, true); o += 4
    dv.setUint32(o, this.matchSeed, true); o += 4
    out[o++] = modeToU8(this.gameMode)
    out[o++] = this.toroidalMap ? 1 : 0
    out[o++] = pc

    for (const id of roster) {
      const idb = te.encode(String(id))
      out[o++] = Math.min(255, idb.length)
      out.set(idb, o); o += idb.length
    }

    dv.setUint16(o, extBytes.length, true); o += 2
    out.set(extBytes, o); o += extBytes.length

    for (let t = 0; t < duration; t++) {
      const row = this.ticks[t]
      for (let p = 0; p < pc; p++) {
        const cell = row[p] ?? { left: false, right: false, up: false, down: false, firePresses: 0 }
        const enc = encodeKeys(cell)
        out[o++] = enc[0]
        out[o++] = enc[1]
      }
    }

    dv.setUint32(o, this.deaths.length, true); o += 4
    for (const d of this.deaths) {
      dv.setUint32(o, d.tick, true); o += 4
      const idb = te.encode(d.playerId)
      const nb = te.encode(d.name)
      const kb = te.encode(String(d.hitBy ?? ''))
      out[o++] = Math.min(255, idb.length)
      out.set(idb, o); o += idb.length
      dv.setUint16(o, nb.length, true); o += 2
      out.set(nb, o); o += nb.length
      out[o++] = Math.min(255, kb.length)
      out.set(kb, o); o += kb.length
    }

    return out
  }
}

export function decodeReplayBinary(buf) {
  if (buf.length < 19) throw new Error('replay too small')
  if (!isReplayAstrHeader(buf)) throw new Error('bad replay magic')
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength)
  let o = 4
  o += 2 // flags

  const tickRateHz = dv.getUint16(o, true); o += 2
  const durationTicks = dv.getUint32(o, true); o += 4
  const matchSeed = dv.getUint32(o, true); o += 4
  const gameMode = u8ToMode(buf[o]); o += 1
  const toroidalMap = buf[o++] !== 0
  const playerCount = buf[o++]

  const rosterIds = []
  const td = new TextDecoder()
  for (let i = 0; i < playerCount; i++) {
    const len = buf[o++]
    rosterIds.push(td.decode(buf.subarray(o, o + len)))
    o += len
  }

  const extLen = dv.getUint16(o, true); o += 2
  if (o + extLen > buf.length) throw new Error('replay truncated extension')
  const extJson = td.decode(buf.subarray(o, o + extLen)); o += extLen
  const extension = JSON.parse(extJson)

  const bytesPerPlayer = 2
  const rowBytes = playerCount * bytesPerPlayer
  const tickBytes = durationTicks * rowBytes
  if (o + tickBytes > buf.length) throw new Error('replay truncated ticks')

  const ticks = []
  for (let t = 0; t < durationTicks; t++) {
    const row = []
    for (let p = 0; p < playerCount; p++) {
      row.push(decodeKeys(buf[o], buf[o + 1]))
      o += 2
    }
    ticks.push(row)
  }

  const deaths = []
  if (o + 4 <= buf.length) {
    const deathCount = dv.getUint32(o, true); o += 4
    for (let i = 0; i < deathCount; i++) {
      if (o + 5 > buf.length) break
      const tick = dv.getUint32(o, true); o += 4
      const idLen = buf[o++]
      const playerId = td.decode(buf.subarray(o, o + idLen)); o += idLen
      const nameLen = dv.getUint16(o, true); o += 2
      const name = td.decode(buf.subarray(o, o + nameLen)); o += nameLen
      const rec = { tick, playerId, name }
      if (o >= buf.length) break
      const kbLen = buf[o++]
      if (kbLen && o + kbLen <= buf.length) {
        rec.hitBy = td.decode(buf.subarray(o, o + kbLen)); o += kbLen
      }
      deaths.push(rec)
    }
  }

  const policySnapshot = extension?.policy ?? {}
  const playersWire = extension?.players?.length
    ? extension.players
    : syntheticPlayersFromRoster(rosterIds)

  return {
    tickRateHz,
    durationTicks,
    matchSeed,
    gameMode,
    toroidalMap,
    rosterIds,
    policySnapshot,
    players: playersWire,
    ticks,
    deaths
  }
}
